// Centralized, human-written content for the marketing/info pages.
// Kept here so copy lives in one place and pages stay declarative.

export interface SourceText {
  title: string;
  sanskrit?: string;
  attribution: string;
  period: string;
  blurb: string;
  importance: string;
  link: string;
  accent: string; // gradient accent for the spine/cover
}

// Foundational classical texts that *inform* the project's curated reference
// notes. The assistant does not reproduce these works; it draws on a small,
// hand-written knowledge base that summarizes widely-taught principles and
// cites the tradition these ideas come from. Presented here as recommended
// reading and historical context.
export const SOURCE_TEXTS: SourceText[] = [
  {
    title: 'Charaka Samhita',
    sanskrit: 'चरक संहिता',
    attribution: 'Attributed to Acharya Charaka',
    period: 'c. 1st–2nd century CE (older oral tradition)',
    blurb:
      'A foundational treatise on internal medicine (kayachikitsa). It frames health as a balance of body, mind, and environment rather than the absence of disease.',
    importance:
      'Its emphasis on diet, daily routine, and prevention shapes how the project organizes everyday wellness guidance.',
    link: 'https://en.wikipedia.org/wiki/Charaka_Samhita',
    accent: 'linear-gradient(160deg, #10b981, #0ea5e9)',
  },
  {
    title: 'Sushruta Samhita',
    sanskrit: 'सुश्रुत संहिता',
    attribution: 'Attributed to Acharya Sushruta',
    period: 'c. 6th century BCE – 4th century CE',
    blurb:
      'Best known for early surgical descriptions, it also details herbal preparations, anatomy, and the classification of plants and minerals.',
    importance:
      'A reference point for how remedies and herbal properties are described and grouped.',
    link: 'https://en.wikipedia.org/wiki/Sushruta_Samhita',
    accent: 'linear-gradient(160deg, #6366f1, #8b5cf6)',
  },
  {
    title: 'Ashtanga Hridayam',
    sanskrit: 'अष्टांग हृदयम्',
    attribution: 'Vagbhata',
    period: 'c. 7th century CE',
    blurb:
      'A concise synthesis of the earlier classics, organized into eight branches. Prized for its clarity and memorable verse.',
    importance:
      'Its structured summaries influence how topics are scoped into short, practical answers.',
    link: 'https://en.wikipedia.org/wiki/V%C4%81gbha%E1%B9%ADa',
    accent: 'linear-gradient(160deg, #f59e0b, #ef4444)',
  },
  {
    title: 'Bhavaprakasha',
    sanskrit: 'भावप्रकाश',
    attribution: 'Bhavamisra',
    period: 'c. 16th century CE',
    blurb:
      'A later compendium notable for its materia medica (nighantu) cataloguing herbs, foods, and their attributed qualities.',
    importance:
      'A model for describing individual herbs and their traditional uses and cautions.',
    link: 'https://en.wikipedia.org/wiki/Bhavamishra',
    accent: 'linear-gradient(160deg, #14b8a6, #22c55e)',
  },
  {
    title: 'Yoga Sutras of Patanjali',
    sanskrit: 'योग सूत्र',
    attribution: 'Patanjali',
    period: 'c. 2nd century BCE – 4th century CE',
    blurb:
      'The classical text on the practice and philosophy of yoga — attention, breath, and steadiness of mind.',
    importance:
      'Grounds the lifestyle and mindfulness side of guidance, beyond herbs and diet.',
    link: 'https://en.wikipedia.org/wiki/Yoga_Sutras_of_Patanjali',
    accent: 'linear-gradient(160deg, #8b5cf6, #ec4899)',
  },
];

export interface Pillar {
  glyph: string;
  title: string;
  body: string;
}

export const PILLARS: Pillar[] = [
  {
    glyph: '🌿',
    title: 'Herbs & remedies',
    body: 'Plain explanations of common herbs — what they are traditionally used for, and the cautions that matter.',
  },
  {
    glyph: '🧭',
    title: 'Dosha & constitution',
    body: 'Understand Vata, Pitta, and Kapha, and how the classics relate them to daily habits and seasons.',
  },
  {
    glyph: '🍵',
    title: 'Diet & routine',
    body: 'Food, sleep, and daily rhythm (dinacharya) — the everyday foundations the texts return to again and again.',
  },
  {
    glyph: '🪷',
    title: 'Mind & practice',
    body: 'Breath, attention, and steadiness — the lifestyle side drawn from yoga and the wellness tradition.',
  },
];

export interface FaqItem {
  q: string;
  a: string;
}

export const FAQS: FaqItem[] = [
  {
    q: 'Is HerbiGPT a substitute for a doctor?',
    a: 'No. It is a reference companion for learning about Ayurvedic ideas. For symptoms, diagnosis, or treatment, speak with a qualified practitioner or physician.',
  },
  {
    q: 'Where do the answers come from?',
    a: 'Responses are generated by a language model, grounded by a small, hand-curated knowledge base that summarizes widely-taught principles and notes the classical tradition behind them.',
  },
  {
    q: 'Are the classical texts included verbatim?',
    a: 'No. The texts listed in the library are foundational references and recommended reading. The project does not reproduce their full contents; it draws on summarized principles.',
  },
  {
    q: 'Does it remember my conversation?',
    a: 'A session keeps your current conversation in the browser so follow-ups make sense. Nothing is stored on a server between visits.',
  },
  {
    q: 'Can I use it on my phone?',
    a: 'Yes. Every page is built to work from a small phone screen up to a wide desktop display.',
  },
];

export interface Contributor {
  name: string;
  role: string;
  points: string[];
  initials: string;
  accent: string;
}

export const CONTRIBUTORS: Contributor[] = [
  {
    name: 'Sumit Chauhan',
    role: 'Project lead · Retrieval & backend',
    initials: 'SC',
    accent: 'linear-gradient(160deg, #10b981, #0ea5e9)',
    points: [
      'Designed the retrieval-grounded answer pipeline',
      'Curated the reference knowledge base',
      'Built and deployed the service architecture',
    ],
  },
  {
    name: 'Xaomiung Codie',
    role: 'Frontend · Product & integration',
    initials: 'XC',
    accent: 'linear-gradient(160deg, #6366f1, #8b5cf6)',
    points: [
      'Shaped the interface and interaction design',
      'Connected the experience to the answer service',
      'Refined responsiveness and motion details',
    ],
  },
];

export const GITHUB_URL =
  'https://github.com/Gosling-dude/HerbiGPT-AI-Powered-Ayurveda-Chatbot';

export interface SuggestPrompt {
  label: string;
  prompt: string;
}

export const STARTER_PROMPTS: SuggestPrompt[] = [
  { label: 'Find my constitution', prompt: 'How do I figure out my dosha type?' },
  { label: 'Calm & sleep', prompt: 'Which herbs are traditionally used for stress and sleep?' },
  { label: 'Everyday energy', prompt: 'What does Ayurveda suggest for steady daily energy?' },
  { label: 'Digestion', prompt: 'How is digestion (agni) supported in Ayurveda?' },
];

// Lightweight follow-up suggestions surfaced after an answer.
export const FOLLOW_UPS: string[] = [
  'What foods suit this best?',
  'Are there any cautions I should know?',
  'How does this change by season?',
  'What daily routine supports this?',
];
